import React, { CSSProperties, forwardRef } from 'react';
import { DayPicker as ReactDayPicker, DayPickerProps } from 'react-day-picker';
import { format } from 'date-fns';
import styled, { css } from 'styled-components';

// eslint-disable-next-line import/no-unresolved
import 'react-day-picker/style.css';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import CalendarMonthCaption from './CalendarMonthCaption';
import CalendarNavigationButton from './CalendarNavigationButton';

type Props = DayPickerProps & {
  isLoading?: boolean;
  containerStyles?: CSSProperties;
  /** Prepended to `day-picker-wrapper` / `day-picker-wrapper-loading` so a specific caller
   *  (e.g. the schedule view) can scope its own styles without leaking into other consumers. */
  classNamePrefix?: string;
};

const DayPicker = forwardRef<HTMLDivElement, Props>(
  (
    {
      classNamePrefix = '',
      containerStyles,
      isLoading = false,
      ...dayPickerProps
    },
    ref,
  ) => {
    const { isRTL, locale } = useGlobalDateLocale();
    return (
      <CalendarContainer
        ref={ref}
        isLoading={isLoading}
        style={containerStyles}
        className={
          isLoading
            ? `${classNamePrefix}day-picker-wrapper-loading`
            : `${classNamePrefix}day-picker-wrapper`
        }
      >
        <ReactDayPicker
          locale={locale}
          navLayout="around"
          animate
          components={{
            MonthCaption: CalendarMonthCaption,
            PreviousMonthButton: (props) => (
              <CalendarNavigationButton
                direction={!isRTL ? 'prev' : 'next'}
                {...props}
              />
            ),
            NextMonthButton: (props) => (
              <CalendarNavigationButton
                direction={!isRTL ? 'next' : 'prev'}
                {...props}
              />
            ),
            ...dayPickerProps.components,
          }}
          modifiersClassNames={{
            range_start: 'range-picker-date-start',
            range_end: 'range-picker-date-end',
            range_middle: 'range-picker-date-middle',
            selected: 'selected--date',
            disabled: isLoading ? '' : 'disabled--date',
            ...dayPickerProps.modifiersClassNames,
          }}
          formatters={{
            formatWeekdayName: (weekday) => format(weekday, 'EEE', { locale }),
            formatDay: (day) => format(day, 'd', { locale }),
            ...dayPickerProps.formatters,
          }}
          startMonth={new Date()}
          {...dayPickerProps}
        />
      </CalendarContainer>
    );
  },
);

const CalendarContainer = styled.div<{ isLoading: boolean }>`
  background-color: var(--way-palette-white-100);
  box-shadow: var(--way-design-boxShadow-l);
  padding: 24px;
  width: max-content;
  cursor: default;

  ${({ isLoading, className }) =>
    isLoading
      ? css`
          &.${className} {
            .rdp-day {
              background-color: var(--way-palette-black-5);
              color: var(--way-palette-black-60);
              opacity: 0.6;
              animation: load 1s infinite;
              @keyframes load {
                50% {
                  opacity: 0.4;
                }
                100% {
                  opacity: 1;
                }
              }
            }
          }
        `
      : css`
          &.${className} {
            .rdp-day {
              color: var(--way-palette-black-100);
              background-color: transparent;
              animation: none;
            }

            & .disabled--date {
              pointer-events: none;
              color: var(--way-palette-black-40);
              text-decoration: line-through;
              cursor: not-allowed;
            }

            & .range-picker-date-start,
            & .range-picker-date-end,
            & .selected--date {
              background-color: var(--way-colors-buttonColorShades-100);
              color: var(--way-colors-buttonColorContrastShades-100);
            }

            & .range-picker-date-middle,
            .rdp-day:not(.disabled--date):not(.selected--date):hover {
              background-color: var(--way-palette-black-5);
              color: var(--way-palette-black-100);
            }
          }
        `}

  & .rdp-day_button {
    border: none;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    font-family: ${({ theme }) => theme.font.primaryFont};
  }

  & .rdp-weekday {
    font-size: 13px;
    line-height: 18px;
    color: var(--way-palette-black-80);
  }

  & .rdp-outside > button {
    cursor: default;
  }
`;

export default DayPicker;
